import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import io from 'socket.io-client'

import {
  QUERY_KEYS,
  WS_EVENTS_KEYS,
  WS_NAMESPACES_KEYS,
  WS_TRANSACTION_STATUS,
} from 'src/constants'

const SERVER_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000/api'

interface UserTransactionStatus {
  scanId: string
  status: WS_TRANSACTION_STATUS | null
  txHash?: string
}

export const useUserTransactionStatus = (scanId: string) => {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState(WS_TRANSACTION_STATUS.IDLE)
  const [txHash, setTxHash] = useState<string | null>(null)

  useEffect(() => {
    // Create and configure the socket connection
    const socketIo = io(`${SERVER_URL}/${WS_NAMESPACES_KEYS.EVENTS_NAMESPACE}`, {
      transports: ['websocket'],
      query: {
        scanId,
      },
    })

    // Join the room for the specific scanId
    socketIo.emit(WS_EVENTS_KEYS.SCAN_USER_TRANSACTION_EVENT, { scanId })

    // Handle incoming status updates
    const handleStatusUpdate = (data: UserTransactionStatus) => {
      if (data.scanId === scanId) {
        if (data.status === WS_TRANSACTION_STATUS.IDLE) {
          return
        }

        setStatus(data.status ? data.status : WS_TRANSACTION_STATUS.IDLE)

        if (data.status === WS_TRANSACTION_STATUS.PENDING) {
          return
        }

        if (
          data.status === WS_TRANSACTION_STATUS.FAILED ||
          data.status === WS_TRANSACTION_STATUS.SUCCESS
        ) {
          if (data?.txHash) {
            setTxHash(data.txHash)
          }

          queryClient.invalidateQueries([QUERY_KEYS.SCANS])
          socketIo.disconnect()
        }
      }
    }

    socketIo.on(WS_EVENTS_KEYS.SCAN_USER_TRANSACTION_EVENT_UPDATE, handleStatusUpdate)

    // Clean up on unmount
    return () => {
      socketIo.off(WS_EVENTS_KEYS.SCAN_USER_TRANSACTION_EVENT_UPDATE, handleStatusUpdate)
      socketIo.disconnect()
    }
  }, [scanId])

  return { status, txHash }
}
